import {
  ArrowsDownUp,
  CaretDown,
  CaretUp
} from "@phosphor-icons/react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent } from "react";
import { cn } from "@/lib/cn";
import { TableEmptyState } from "./TableEmptyState";
import { TableErrorState } from "./TableErrorState";
import { TablePagination } from "./TablePagination";
import { TableSkeleton } from "./TableSkeleton";
import type { DataTableColumn, DataTableProps, DataTableSorting } from "./DataTable.types";

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

const tableMetricClassName = {
  header: "bfrs-h-[46px] bfrs-px-5 bfrs-py-[9px]",
  cell: "bfrs-h-16 bfrs-px-5 bfrs-py-4",
  text: "bfrs-text-[12px]"
};

const defaultColumnWidth = 144;
const defaultLeadingColumnWidth = 220;
const selectionColumnWidth = 44;
const defaultRowActionsWidth = 146;
const selectionCheckboxClassName = "bfrs-size-3 bfrs-shrink-0 bfrs-cursor-pointer bfrs-accent-primary";
const headerTextClassName = "bfrs-text-[12px] bfrs-font-bold bfrs-tracking-[0.24px] bfrs-text-text-primary";

function pinButtonClassName(pinned: boolean) {
  return cn(
    "bfrs-ml-1 bfrs-inline-flex bfrs-h-[17px] bfrs-w-10 bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-sm bfrs-px-1.5 bfrs-text-[9px] bfrs-font-bold bfrs-normal-case bfrs-leading-[1.5] bfrs-tracking-normal bfrs-opacity-0 bfrs-transition group-hover:bfrs-opacity-100 focus-visible:bfrs-opacity-100 focus-visible:bfrs-outline-none focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus",
    pinned ? "bfrs-bg-success-soft bfrs-text-success-contrast" : "bfrs-bg-surface-raised bfrs-text-text-secondary hover:bfrs-text-text-primary"
  );
}

function getValue<Row>(row: Row, column: DataTableColumn<Row>) {
  if (column.accessorFn) return column.accessorFn(row);
  if (column.accessorKey) return row[column.accessorKey];
  return undefined;
}

function getSortValue<Row>(row: Row, column: DataTableColumn<Row>) {
  if (column.sortValue) return column.sortValue(row);
  return getValue(row, column);
}

function compareValues(left: unknown, right: unknown) {
  if (typeof left === "number" && typeof right === "number") return left - right;
  return String(left ?? "").localeCompare(String(right ?? ""), undefined, { numeric: true, sensitivity: "base" });
}

function toPixelWidth(width: number | string | undefined, fallback: number) {
  if (typeof width === "number") return width;
  if (typeof width === "string" && width.endsWith("px")) {
    const parsed = Number.parseFloat(width);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

function syncColumnOrder(current: string[], columnIds: string[]) {
  const known = current.filter((id) => columnIds.includes(id));
  const missing = columnIds.filter((id) => !known.includes(id));
  return [...known, ...missing];
}

function moveItem(items: string[], draggedId: string, targetId: string) {
  const next = [...items];
  const from = next.indexOf(draggedId);
  const to = next.indexOf(targetId);
  if (from === -1 || to === -1 || from === to) return items;
  next.splice(from, 1);
  next.splice(to, 0, draggedId);
  return next;
}

type MeasuredTableWidths = {
  selection: number;
  leading: number;
  columns: number[];
  rowActions: number;
};

function roundMeasuredWidth(width: number) {
  return Math.round(width * 100) / 100;
}

function measuredOrFallback(width: number | undefined, fallback: number) {
  return width && width > 0 ? width : fallback;
}

function sameMeasuredWidths(current: MeasuredTableWidths | null, next: MeasuredTableWidths) {
  if (!current) return false;
  if (current.columns.length !== next.columns.length) return false;
  if (
    Math.abs(current.selection - next.selection) > 0.5 ||
    Math.abs(current.leading - next.leading) > 0.5 ||
    Math.abs(current.rowActions - next.rowActions) > 0.5
  ) {
    return false;
  }
  return current.columns.every((width, index) => Math.abs(width - next.columns[index]) <= 0.5);
}

export function DataTable<Row>({
  data,
  columns,
  getRowId,
  loading,
  error,
  emptyState,
  emptyTitle,
  emptyDescription,
  pagination,
  enableSorting = true,
  sorting,
  defaultSorting = null,
  onSortingChange,
  visibleColumnIds,
  columnOrder,
  defaultColumnOrder,
  onColumnOrderChange,
  enableColumnPinning = true,
  pinnedColumnCount,
  defaultPinnedColumnCount = 0,
  onPinnedColumnCountChange,
  pinLeadingColumn,
  defaultPinLeadingColumn = false,
  onPinLeadingColumnChange,
  enableColumnReordering = true,
  resetKey,
  showSortIcon = "always",
  leadingColumn,
  rowActions,
  rowActionsHeader = "Actions",
  rowActionsWidth = defaultRowActionsWidth,
  stickyRowActions = Boolean(rowActions),
  selection,
  selectedRowIds = [],
  onSelectedRowIdsChange,
  stickyHeader = true,
  minWidth = "100%",
  maxHeight,
  className,
  tableClassName,
  headerClassName,
  rowClassName,
  cellClassName,
  onRowClick,
  "aria-label": ariaLabel = "Data table"
}: DataTableProps<Row>) {
  const tableRef = useRef<HTMLTableElement>(null);
  const [internalSorting, setInternalSorting] = useState<DataTableSorting | null>(defaultSorting);
  const [internalColumnOrder, setInternalColumnOrder] = useState(() => defaultColumnOrder ?? columns.map((column) => column.id));
  const [internalPinnedColumnCount, setInternalPinnedColumnCount] = useState(defaultPinnedColumnCount);
  const [internalPinLeadingColumn, setInternalPinLeadingColumn] = useState(defaultPinLeadingColumn);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [measuredWidths, setMeasuredWidths] = useState<MeasuredTableWidths | null>(null);

  const activeSorting = enableSorting ? (sorting === undefined ? internalSorting : sorting) : null;
  const columnIds = useMemo(() => columns.map((column) => column.id), [columns]);
  const columnById = useMemo(() => new Map(columns.map((column) => [column.id, column])), [columns]);

  useEffect(() => {
    if (columnOrder !== undefined) return;
    setInternalColumnOrder((current) => syncColumnOrder(current, columnIds));
  }, [columnIds, columnOrder]);

  useEffect(() => {
    if (resetKey === undefined) return;
    if (sorting === undefined) setInternalSorting(defaultSorting);
    if (columnOrder === undefined) setInternalColumnOrder(syncColumnOrder(defaultColumnOrder ?? columnIds, columnIds));
    if (pinnedColumnCount === undefined) setInternalPinnedColumnCount(defaultPinnedColumnCount);
    if (pinLeadingColumn === undefined) setInternalPinLeadingColumn(defaultPinLeadingColumn);
    setDragIndex(null);
    setDropIndex(null);
  }, [
    columnIds,
    columnOrder,
    defaultColumnOrder,
    defaultPinnedColumnCount,
    defaultPinLeadingColumn,
    defaultSorting,
    pinLeadingColumn,
    pinnedColumnCount,
    resetKey,
    sorting
  ]);

  const orderedColumns = useMemo(() => {
    const orderedIds = syncColumnOrder(columnOrder ?? internalColumnOrder, columnIds);
    return orderedIds
      .map((id) => columnById.get(id))
      .filter((column): column is DataTableColumn<Row> => Boolean(column));
  }, [columnById, columnIds, columnOrder, internalColumnOrder]);

  const displayColumns = useMemo(() => {
    if (!visibleColumnIds) return orderedColumns;
    const visibleIds = new Set(visibleColumnIds);
    return orderedColumns.filter((column) => visibleIds.has(column.id));
  }, [orderedColumns, visibleColumnIds]);

  const declaredDisplayColumnWidths = useMemo(
    () => displayColumns.map((column) => toPixelWidth(column.width ?? column.minWidth, defaultColumnWidth)),
    [displayColumns]
  );

  const activePinnedColumnCount = enableColumnPinning
    ? Math.min(
        displayColumns.length,
        Math.max(0, pinnedColumnCount === undefined ? internalPinnedColumnCount : pinnedColumnCount)
      )
    : 0;
  const activePinLeadingColumn = enableColumnPinning && leadingColumn
    ? pinLeadingColumn === undefined ? internalPinLeadingColumn : pinLeadingColumn
    : false;
  // The leading column sits to the left of every data column, so it must stay
  // pinned whenever any data column is pinned — otherwise it scrolls away and
  // leaves the pinned data columns detached from the row's identity column.
  const leadingColumnIsPinned = Boolean(leadingColumn) && (activePinLeadingColumn || activePinnedColumnCount > 0);
  // The edge shadow marks the right boundary of the pinned block, so it belongs
  // only to the rightmost pinned column. The leading column is rightmost only
  // when no data columns are pinned to its right.
  const leadingColumnShowsEdgeShadow = leadingColumnIsPinned && activePinnedColumnCount === 0;
  const hasStandaloneSelection = Boolean(selection && !leadingColumn);
  const selectionIsSticky = hasStandaloneSelection && activePinnedColumnCount > 0;
  const leadingColumnWidth = toPixelWidth(leadingColumn?.width ?? leadingColumn?.minWidth, defaultLeadingColumnWidth);
  const measuredDisplayColumnWidths = measuredWidths?.columns.length === displayColumns.length ? measuredWidths.columns : undefined;
  const stickySelectionWidth = measuredOrFallback(measuredWidths?.selection, selectionColumnWidth);
  const stickyLeadingColumnWidth = measuredOrFallback(measuredWidths?.leading, leadingColumnWidth);
  const baseStickyLeft = (selectionIsSticky ? stickySelectionWidth : 0) + (leadingColumnIsPinned ? stickyLeadingColumnWidth : 0);
  const visibleColumnCount = displayColumns.length + (hasStandaloneSelection ? 1 : 0) + (leadingColumn ? 1 : 0) + (rowActions ? 1 : 0);

  const rowId = (row: Row, index: number) => getRowId?.(row, index) ?? String((row as { id?: string | number }).id ?? index);

  const measureTableWidths = useCallback(() => {
    const headerRow = tableRef.current?.tHead?.rows[0];
    if (!headerRow) return;

    const cells = Array.from(headerRow.cells);
    let cellIndex = 0;
    const readCellWidth = () => roundMeasuredWidth(cells[cellIndex++]?.getBoundingClientRect().width ?? 0);
    const nextWidths: MeasuredTableWidths = {
      selection: hasStandaloneSelection ? readCellWidth() : 0,
      leading: leadingColumn ? readCellWidth() : 0,
      columns: displayColumns.map(() => readCellWidth()),
      rowActions: rowActions ? readCellWidth() : 0
    };

    setMeasuredWidths((current) => sameMeasuredWidths(current, nextWidths) ? current : nextWidths);
  }, [displayColumns, hasStandaloneSelection, leadingColumn, rowActions]);

  useIsomorphicLayoutEffect(() => {
    measureTableWidths();
  });

  useIsomorphicLayoutEffect(() => {
    const table = tableRef.current;
    if (!table || typeof window === "undefined" || typeof window.ResizeObserver === "undefined") return undefined;

    const observer = new window.ResizeObserver(measureTableWidths);
    observer.observe(table);
    Array.from(table.tHead?.rows[0]?.cells ?? []).forEach((cell) => observer.observe(cell));

    return () => observer.disconnect();
  }, [measureTableWidths]);

  const sortedData = useMemo(() => {
    if (!activeSorting) return data;
    const column = columns.find((item) => item.id === activeSorting.columnId);
    if (!column) return data;
    return [...data].sort((left, right) => {
      const result = compareValues(getSortValue(left, column), getSortValue(right, column));
      return activeSorting.direction === "asc" ? result : -result;
    });
  }, [activeSorting, columns, data]);

  const allSelected = sortedData.length > 0 && sortedData.every((row, index) => selectedRowIds.includes(rowId(row, index)));

  const updateSorting = (column: DataTableColumn<Row>) => {
    if (!enableSorting || !column.sortable) return;
    const next: DataTableSorting =
      activeSorting?.columnId === column.id
        ? { columnId: column.id, direction: activeSorting.direction === "asc" ? "desc" : "asc" }
        : { columnId: column.id, direction: "asc" };
    if (sorting === undefined) setInternalSorting(next);
    onSortingChange?.(next);
  };

  const updateColumnOrder = (next: string[]) => {
    if (columnOrder === undefined) setInternalColumnOrder(next);
    onColumnOrderChange?.(next);
  };

  const updatePinnedColumnCount = (count: number) => {
    const next = Math.min(displayColumns.length, Math.max(0, count));
    if (pinnedColumnCount === undefined) setInternalPinnedColumnCount(next);
    onPinnedColumnCountChange?.(next);
  };

  const updatePinLeadingColumn = (pinned: boolean) => {
    if (pinLeadingColumn === undefined) setInternalPinLeadingColumn(pinned);
    onPinLeadingColumnChange?.(pinned);
  };

  const toggleAllRows = () => {
    if (!onSelectedRowIdsChange) return;
    onSelectedRowIdsChange(allSelected ? [] : sortedData.map((row, index) => rowId(row, index)));
  };

  const toggleRow = (id: string) => {
    if (!onSelectedRowIdsChange) return;
    onSelectedRowIdsChange(selectedRowIds.includes(id) ? selectedRowIds.filter((selectedId) => selectedId !== id) : [...selectedRowIds, id]);
  };

  const getStickyLeft = (idx: number) => {
    let left = baseStickyLeft;
    for (let i = 0; i < idx; i += 1) {
      left += measuredOrFallback(measuredDisplayColumnWidths?.[i], declaredDisplayColumnWidths[i] ?? defaultColumnWidth);
    }
    return left;
  };

  const handleDragStart = (event: DragEvent, idx: number) => {
    if (!enableColumnReordering) return;
    setDragIndex(idx);
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event: DragEvent, idx: number) => {
    if (!enableColumnReordering) return;
    event.preventDefault();
    setDropIndex(idx);
  };

  const handleDrop = (event: DragEvent, idx: number) => {
    if (!enableColumnReordering) return;
    event.preventDefault();
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null);
      setDropIndex(null);
      return;
    }

    const draggedId = displayColumns[dragIndex]?.id;
    const targetId = displayColumns[idx]?.id;
    if (draggedId && targetId) {
      updateColumnOrder(moveItem(columnOrder ?? internalColumnOrder, draggedId, targetId));
      if (dragIndex < activePinnedColumnCount) updatePinnedColumnCount(Math.min(activePinnedColumnCount, idx + 1));
    }
    setDragIndex(null);
    setDropIndex(null);
  };

  const stickyHeaderCell = (left: number, zIndex = 20): CSSProperties => ({
    position: "sticky",
    left,
    zIndex
  });

  const stickyBodyCell = (left: number, zIndex = 10): CSSProperties => ({
    position: "sticky",
    left,
    zIndex
  });

  const stickyActionStyle: CSSProperties = stickyRowActions
    ? {
        position: "sticky",
        right: 0,
        zIndex: 12
      }
    : {};

  return (
    <div className={cn("bfrs-overflow-hidden bfrs-rounded-md bfrs-border bfrs-border-border-strong bfrs-bg-surface-card", className)}>
      <div className="bfrs-overflow-auto" style={maxHeight !== undefined ? { maxHeight } : undefined}>
        <table ref={tableRef} aria-label={ariaLabel} aria-busy={loading || undefined} className={cn("bfrs-w-full bfrs-border-separate bfrs-border-spacing-0 bfrs-text-left", tableClassName)} style={{ minWidth, tableLayout: "fixed" }}>
          <thead className={cn(stickyHeader && "bfrs-sticky bfrs-top-0 bfrs-z-30", headerClassName)}>
            <tr className="bfrs-bg-surface-app">
              {hasStandaloneSelection && (
                <th
                className={cn("bfrs-border-b bfrs-border-border-strong bfrs-bg-surface-app bfrs-text-center bfrs-text-text-secondary", tableMetricClassName.header, selectionIsSticky && "bfrs-shadow-[3px_0_8px_rgba(0,0,0,0.05)]")}
                  style={{ width: selectionColumnWidth, minWidth: selectionColumnWidth, ...(selectionIsSticky ? stickyHeaderCell(0, 24) : {}) }}
                >
                  <div className="bfrs-flex bfrs-items-center bfrs-justify-center">
                    <input type="checkbox" checked={allSelected} onChange={toggleAllRows} aria-label="Select all rows" className={selectionCheckboxClassName} />
                  </div>
                </th>
              )}

              {leadingColumn && (
                <th
                  scope="col"
                  className={cn(
                    "bfrs-group bfrs-border-b bfrs-border-border-strong bfrs-bg-surface-app bfrs-align-middle",
                    headerTextClassName,
                    tableMetricClassName.header,
                    leadingColumnShowsEdgeShadow && "bfrs-shadow-[3px_0_8px_rgba(0,0,0,0.05)]",
                    leadingColumn.headerClassName
                  )}
                  style={{
                    width: leadingColumn.width,
                    minWidth: leadingColumn.minWidth ?? leadingColumn.width ?? defaultLeadingColumnWidth,
                    ...(leadingColumnIsPinned ? stickyHeaderCell(selectionIsSticky ? selectionColumnWidth : 0, 23) : {}),
                    ...leadingColumn.headerStyle
                  }}
                >
                  <span className="bfrs-inline-flex bfrs-max-w-full bfrs-items-center bfrs-gap-2">
                    {selection && (
                      <input type="checkbox" checked={allSelected} onChange={toggleAllRows} aria-label="Select all rows" className={selectionCheckboxClassName} />
                    )}
                    <span className="bfrs-min-w-0 bfrs-truncate">{leadingColumn.header}</span>
                    {enableColumnPinning && (
                      <button
                        type="button"
                        className={pinButtonClassName(leadingColumnIsPinned)}
                        title={leadingColumnIsPinned ? "Unpin leading column" : "Pin leading column"}
                        aria-label={leadingColumnIsPinned ? "Unpin leading column" : "Pin leading column"}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (leadingColumnIsPinned) {
                            updatePinLeadingColumn(false);
                            updatePinnedColumnCount(0);
                          } else {
                            updatePinLeadingColumn(true);
                          }
                        }}
                      >
                        {leadingColumnIsPinned ? "Unpin" : "Pin"}
                      </button>
                    )}
                  </span>
                </th>
              )}

              {displayColumns.map((column, idx) => {
                const sorted = activeSorting?.columnId === column.id;
                const pinned = idx < activePinnedColumnCount;
                const lastPinned = idx === activePinnedColumnCount - 1;
                const headerStyle: CSSProperties = {
                  width: column.width,
                  minWidth: column.minWidth ?? column.width,
                  textAlign: column.align,
                  ...(pinned ? stickyHeaderCell(getStickyLeft(idx), 22) : {}),
                  ...column.headerStyle
                };
                const columnSortable = enableSorting && column.sortable;
                const showInactiveSortIcon = columnSortable && showSortIcon === "always" && !sorted;
                const showActiveSortIcon = columnSortable && sorted && showSortIcon !== "never";

                return (
                  <th
                    key={column.id}
                    scope="col"
                    aria-sort={columnSortable ? (sorted ? (activeSorting.direction === "asc" ? "ascending" : "descending") : "none") : undefined}
                    draggable={enableColumnReordering}
                    onDragStart={(event) => handleDragStart(event, idx)}
                    onDragOver={(event) => handleDragOver(event, idx)}
                    onDrop={(event) => handleDrop(event, idx)}
                    onDragEnd={() => {
                      setDragIndex(null);
                      setDropIndex(null);
                    }}
                    className={cn(
                      "bfrs-group bfrs-border-b bfrs-border-l-2 bfrs-border-border-strong bfrs-border-l-transparent bfrs-bg-surface-app",
                      headerTextClassName,
                      tableMetricClassName.header,
                      columnSortable && "hover:bfrs-text-primary",
                      sorted && "bfrs-text-primary",
                      enableColumnReordering && "bfrs-cursor-grab bfrs-select-none active:bfrs-cursor-grabbing",
                      dropIndex === idx && "bfrs-border-l-primary",
                      pinned && lastPinned && "bfrs-shadow-[3px_0_8px_rgba(0,0,0,0.05)]",
                      column.headerClassName
                    )}
                    style={headerStyle}
                  >
                    <span className={cn("bfrs-inline-flex bfrs-max-w-full bfrs-items-center bfrs-gap-1.5", column.align === "right" && "bfrs-justify-end", column.align === "center" && "bfrs-justify-center")}>
                      {columnSortable ? (
                        <button
                          type="button"
                          className="bfrs-inline-flex bfrs-min-w-0 bfrs-items-center bfrs-gap-1 bfrs-rounded-sm bfrs-outline-none focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus"
                          onClick={() => updateSorting(column)}
                        >
                          <span className="bfrs-min-w-0 bfrs-truncate">{column.header}</span>
                          {showActiveSortIcon && (activeSorting.direction === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />)}
                          {showInactiveSortIcon && <ArrowsDownUp size={12} className="bfrs-text-text-disabled" />}
                        </button>
                      ) : (
                        <span className="bfrs-min-w-0 bfrs-truncate">{column.header}</span>
                      )}
                      {enableColumnPinning && (
                        <button
                          type="button"
                          className={pinButtonClassName(pinned)}
                          title={pinned && lastPinned ? "Unpin columns" : "Pin up to this column"}
                          aria-label={pinned && lastPinned ? "Unpin columns" : `Pin up to ${String(column.header)}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            updatePinnedColumnCount(pinned && lastPinned ? 0 : idx + 1);
                          }}
                        >
                          {pinned ? "Unpin" : "Pin"}
                        </button>
                      )}
                    </span>
                  </th>
                );
              })}

              {rowActions && (
                <th
                  className={cn("bfrs-border-b bfrs-border-border-strong bfrs-bg-surface-app", headerTextClassName, tableMetricClassName.header, stickyRowActions && "bfrs-shadow-[0px_2px_6px_rgba(0,0,0,0.08)]")}
                  style={{ width: rowActionsWidth, minWidth: rowActionsWidth, ...stickyActionStyle, zIndex: stickyRowActions ? 24 : undefined }}
                >
                  {rowActionsHeader}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableSkeleton rows={5} columns={visibleColumnCount} />
            ) : error ? (
              <TableErrorState description={error} colSpan={visibleColumnCount} />
            ) : sortedData.length === 0 ? (
              emptyState ? (
                <tr><td colSpan={visibleColumnCount}>{emptyState}</td></tr>
              ) : (
                <TableEmptyState title={emptyTitle} description={emptyDescription} colSpan={visibleColumnCount} />
              )
            ) : (
              sortedData.map((row, rowIndex) => {
                const id = rowId(row, rowIndex);
                const selected = selectedRowIds.includes(id);
                const hovered = hoveredRowId === id;
                const resolvedRowClassName = typeof rowClassName === "function" ? rowClassName(row, rowIndex) : rowClassName;
                const rowState = { rowIndex, selected, hovered };
                const cellStateClassName = cn("group-hover/row:bfrs-bg-surface-app", selected && "bfrs-bg-surface-app");

                return (
                  <tr
                    key={id}
                    className={cn("bfrs-group/row bfrs-bg-surface-card bfrs-transition hover:bfrs-bg-surface-app", selected && "bfrs-bg-surface-app", onRowClick && "bfrs-cursor-pointer", resolvedRowClassName)}
                    onMouseEnter={() => setHoveredRowId(id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    onClick={() => onRowClick?.(row)}
                  >
                    {hasStandaloneSelection && (
                      <td
                        className={cn("bfrs-border-b bfrs-border-border bfrs-bg-surface-card bfrs-align-middle", tableMetricClassName.cell, cellStateClassName, selectionIsSticky && "bfrs-shadow-[3px_0_8px_rgba(0,0,0,0.05)]")}
                        style={{ width: selectionColumnWidth, minWidth: selectionColumnWidth, ...(selectionIsSticky ? stickyBodyCell(0, 14) : {}) }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <div className="bfrs-flex bfrs-items-center bfrs-justify-center">
                          <input type="checkbox" checked={selected} onChange={() => toggleRow(id)} aria-label={`Select row ${rowIndex + 1}`} className={selectionCheckboxClassName} />
                        </div>
                      </td>
                    )}

                    {leadingColumn && (
                      <td
                        className={cn("bfrs-border-b bfrs-border-border bfrs-bg-surface-card bfrs-align-middle", tableMetricClassName.cell, cellStateClassName, leadingColumnShowsEdgeShadow && "bfrs-shadow-[3px_0_8px_rgba(0,0,0,0.05)]", leadingColumn.className)}
                        style={{
                          width: leadingColumn.width,
                          minWidth: leadingColumn.minWidth ?? leadingColumn.width ?? defaultLeadingColumnWidth,
                          ...(leadingColumnIsPinned ? stickyBodyCell(selectionIsSticky ? selectionColumnWidth : 0, 13) : {}),
                          ...leadingColumn.style
                        }}
                        onClick={selection ? (event) => event.stopPropagation() : undefined}
                      >
                        <div className="bfrs-flex bfrs-items-center bfrs-gap-3">
                          {selection && (
                            <input type="checkbox" checked={selected} onChange={() => toggleRow(id)} aria-label={`Select row ${rowIndex + 1}`} className={selectionCheckboxClassName} />
                          )}
                          <div className="bfrs-min-w-0 bfrs-flex-1">{leadingColumn.cell(row, rowState)}</div>
                        </div>
                      </td>
                    )}

                    {displayColumns.map((column, idx) => {
                      const value = getValue(row, column);
                      const pinned = idx < activePinnedColumnCount;
                      const lastPinned = idx === activePinnedColumnCount - 1;
                      return (
                        <td
                          key={column.id}
                          className={cn(
                            "bfrs-border-b bfrs-border-border bfrs-bg-surface-card bfrs-align-middle bfrs-text-text-secondary",
                            tableMetricClassName.cell, cellStateClassName,
                            tableMetricClassName.text,
                            pinned && lastPinned && "bfrs-shadow-[3px_0_8px_rgba(0,0,0,0.05)]",
                            cellClassName,
                            column.className
                          )}
                          style={{
                            width: column.width,
                            minWidth: column.minWidth ?? column.width,
                            textAlign: column.align,
                            ...(pinned ? stickyBodyCell(getStickyLeft(idx), 12) : {}),
                            ...column.style
                          }}
                        >
                          {column.cell ? column.cell(row, { value, rowIndex, selected, hovered }) : String(value ?? "")}
                        </td>
                      );
                    })}

                    {rowActions && (
                      <td
                        className={cn("bfrs-border-b bfrs-border-border bfrs-bg-surface-card bfrs-align-middle", tableMetricClassName.cell, cellStateClassName, stickyRowActions && "bfrs-shadow-[-3px_0_4px_rgba(0,0,0,0.04)]")}
                        style={{ width: rowActionsWidth, minWidth: rowActionsWidth, ...stickyActionStyle }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {rowActions(row)}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {pagination && <TablePagination {...pagination} />}
    </div>
  );
}
